import React, { useState, useEffect } from 'react';
import { useTheme } from '../utils/ThemeContext';
import { Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, IconButton, InputAdornment } from '@mui/material';
import { 
  Visibility, 
  VisibilityOff,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { getButtonStyles, BUTTON_COLORS } from '../config/buttonStyles';
import '../assets/css/futuristic-modern.css';

function Profile() {
  const [activeTab, setActiveTab] = useState('profile');
  const [userData, setUserData] = useState({
    firstName: '', lastName: '', email: '', phone: '', location: '', timezone: '',
    role: '', institution: '', department: '', specialization: '',
    joinDate: '', lastActive: '', researchInterests: []
  });
  const [editingData, setEditingData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [settings, setSettings] = useState({
    emailNotifications: false,
    researchUpdates: false,
    weeklyDigest: false
  });
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorAuth: false,
    loginNotifications: false
  });

  const { isDarkMode, toggleTheme, isLoading: themeLoading } = useTheme();

  // Snackbar notification state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Password change dialog state
  const [passwordDialog, setPasswordDialog] = useState({
    open: false,
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    showCurrentPassword: false,
    showNewPassword: false,
    showConfirmPassword: false,
    loading: false,
    error: ''
  });

  const showNotification = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  // Password change handlers
  const handleOpenPasswordDialog = () => {
    setPasswordDialog({
      open: true,
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      loading: false,
      error: ''
    });
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialog({ ...passwordDialog, open: false });
  };

  const handlePasswordChange = async () => {
    // Validate passwords
    if (!passwordDialog.currentPassword || !passwordDialog.newPassword || !passwordDialog.confirmPassword) {
      setPasswordDialog({ ...passwordDialog, error: 'All fields are required' });
      return;
    }

    if (passwordDialog.newPassword !== passwordDialog.confirmPassword) {
      setPasswordDialog({ ...passwordDialog, error: 'New passwords do not match' });
      return;
    }

    if (passwordDialog.newPassword.length < 6) {
      setPasswordDialog({ ...passwordDialog, error: 'Password must be at least 6 characters long' });
      return;
    }

    setPasswordDialog({ ...passwordDialog, loading: true, error: '' });

    try {
      const token = localStorage.getItem('authToken');
      await axios.post(
        `${API_BASE_URL}/api/change-password`,
        {
          currentPassword: passwordDialog.currentPassword,
          newPassword: passwordDialog.newPassword
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      showNotification('Password changed successfully!', 'success');
      handleClosePasswordDialog();
    } catch (error) {
      setPasswordDialog({
        ...passwordDialog,
        loading: false,
        error: error.response?.data?.error || 'Failed to change password'
      });
    }
  };
  
  // Debug theme state
  useEffect(() => {
    console.log('🎨 Profile component - isDarkMode:', isDarkMode, 'themeLoading:', themeLoading);
  }, [isDarkMode, themeLoading]);

  // Fix scrolling issues when profile page is active
  useEffect(() => {
    // Add profile-page-active class to body and html
    document.body.classList.add('profile-page-active');
    document.documentElement.classList.add('profile-page-active');
    
    // Add profile-page-active class to root element
    if (document.getElementById('root')) {
      document.getElementById('root').classList.add('profile-page-active');
    }

    return () => {
      // Cleanup: remove profile-page-active class
      document.body.classList.remove('profile-page-active');
      document.documentElement.classList.remove('profile-page-active');
      
      // Remove profile-page-active class from root element
      if (document.getElementById('root')) {
        document.getElementById('root').classList.remove('profile-page-active');
      }
    };
  }, []);

  // Load user data from API
  useEffect(() => {
    const loadProfileData = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          console.error('No auth token found');
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/get-profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.profile) {
            setUserData(prev => ({ ...prev, ...data.profile }));
          }
        } else {
          console.error('Failed to load profile data');
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      }
    };

    loadProfileData();
  }, []);

  const handleEdit = () => {
    setEditingData({ ...userData });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingData({});
  };

  const handleInputChange = (field, value) => {
    setEditingData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        showNotification('Authentication required. Please sign in again.', 'warning');
        return;
      }

      // Prepare data for API (email is excluded - it cannot be changed)
      const profileData = {
        firstName: editingData.firstName || '',
        lastName: editingData.lastName || '',
        role: editingData.role || '',
        phone: editingData.phone || '',
        location: editingData.location || '',
        timezone: editingData.timezone || '',
        institution: editingData.institution || '',
        department: editingData.department || '',
        specialization: editingData.specialization || '',
        researchInterests: editingData.researchInterests || [],
        joinDate: editingData.joinDate || userData.joinDate
      };

      console.log('Sending profile data:', profileData);

      // Save to database via API
      const response = await fetch(`${API_BASE_URL}/api/update-profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(profileData)
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (response.ok) {
        const result = await response.json();
        console.log('Success response:', result);
        
        // Update local state - PRESERVE EMAIL from original userData
        const updatedUserData = { 
          ...editingData, 
          email: userData.email // Preserve email since it can't be changed
        };
        setUserData(updatedUserData);
        
        // Save to localStorage as backup
        localStorage.setItem('userData', JSON.stringify(updatedUserData));
        
        // Update userProfileData in localStorage with new profileComplete status
        const updatedProfile = { 
          ...updatedUserData, 
          profileComplete: Boolean(result.profile?.profileComplete || false) 
        };
        localStorage.setItem('userProfileData', JSON.stringify(updatedProfile));
        
        // Trigger custom event to update navigation notification
        window.dispatchEvent(new CustomEvent('profileUpdated', { 
          detail: { profileData: updatedProfile } 
        }));
        
        setIsEditing(false);
        setEditingData({});
        
        // Show success message
        showNotification('Profile updated successfully!', 'success');
      } else {
        // Get response text for debugging
        const responseText = await response.text();
        console.error('Error response text:', responseText);
        
        let errorMessage = 'Failed to update profile';
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          console.error('Could not parse error response as JSON:', parseError);
          errorMessage = `Server error: ${response.status} - ${responseText.substring(0, 100)}`;
        }
        
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      showNotification(`Error saving profile: ${error.message}`, 'error');
    }
  };

  const handleSettingChange = (setting, value) => {
    setSettings(prev => ({ ...prev, [setting]: value }));
  };

  const handleSecurityChange = (setting, value) => {
    setSecuritySettings(prev => ({ ...prev, [setting]: value }));
  };

  const getProfileCompletionStatus = () => {
    const requiredFields = ['firstName', 'lastName', 'role', 'email', 'phone', 'location', 'institution', 'department', 'specialization'];
    const filledFields = requiredFields.filter(field => userData[field] && userData[field].trim() !== '');
    const percentage = Math.round((filledFields.length / requiredFields.length) * 100);
    
    return {
      percentage,
      isComplete: percentage === 100,
      filledFields: filledFields.length,
      totalFields: requiredFields.length
    };
  };

  const completionStatus = getProfileCompletionStatus();

  useEffect(() => {
    if (completionStatus.isComplete) {
      console.log('Profile completed!');
    }
  }, [completionStatus.isComplete]);

  const renderProfileTab = () => (
    <div className="profile-content">
      {/* Profile Completion Banner */}
      {!completionStatus.isComplete && (
        <div className="completion-banner">
          <div className="completion-info">
            <h3>Complete Your Profile</h3>
            <p>Fill in your information to unlock all features and personalize your experience</p>
            <div className="completion-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${completionStatus.percentage}%` }}
                ></div>
              </div>
              <span className="progress-text">{completionStatus.percentage}% Complete</span>
            </div>
          </div>
          <div className="completion-actions">
            <Button 
              className="btn-modern"
              onClick={() => setIsEditing(true)}
              startIcon={<EditIcon />}
              sx={getButtonStyles(false, isDarkMode, 'generic')}
            >
              Complete Profile
            </Button>
          </div>
        </div>
      )}

      <div className="profile-header">
        <div className="profile-avatar">
          <div className="avatar-placeholder">
            {(isEditing ? editingData.firstName : userData.firstName)?.charAt(0) || 'U'}{(isEditing ? editingData.lastName : userData.lastName)?.charAt(0) || 'S'}
          </div>
          <div className="status-indicator online"></div>
        </div>
        <div className="profile-info">
          <h2 className="profile-name">
            {isEditing ? `${editingData.firstName || ''} ${editingData.lastName || ''}` : `${userData.firstName} ${userData.lastName}`}
          </h2>
          <p className="profile-role">{isEditing ? editingData.role : userData.role}</p>
          <p className="profile-institution">{isEditing ? editingData.institution : userData.institution}</p>
        </div>
        <div className="profile-actions">
          <Button 
            className="btn-modern"
            onClick={isEditing ? handleCancel : handleEdit}
            startIcon={isEditing ? <CancelIcon /> : <EditIcon />}
            sx={getButtonStyles(false, isDarkMode, 'generic')}
          >
            {isEditing ? 'Cancel' : 'Edit Profile'}
          </Button>
          {isEditing && (
            <Button 
              className="btn-modern" 
              onClick={handleSave}
              startIcon={<SaveIcon />}
              sx={getButtonStyles(false, isDarkMode, 'generic')}
            >
              Save Changes
            </Button>
          )}
        </div>
      </div>

      <div className="profile-details">
        <div className="detail-section">
          <h3>Personal Information</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label>First Name</label>
              {isEditing ? (
                <input 
                  type="text" 
                  value={editingData.firstName || ''}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className="input-modern"
                />
              ) : (
                <span>{userData.firstName}</span>
              )}
            </div>
            <div className="detail-item">
              <label>Last Name</label>
              {isEditing ? (
                <input 
                  type="text" 
                  value={editingData.lastName || ''}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className="input-modern"
                />
              ) : (
                <span>{userData.lastName}</span>
              )}
            </div>
            <div className="detail-item">
              <label>Email</label>
              {isEditing ? (
                <input 
                  type="email" 
                  value={userData.email || ''}
                  disabled
                  className="input-modern"
                  style={{ 
                    cursor: 'not-allowed',
                    opacity: 0.7,
                    backgroundColor: 'var(--surface-2)'
                  }}
                  title="Email cannot be changed. This is your login email."
                />
              ) : (
                <span>{userData.email}</span>
              )}
            </div>
            <div className="detail-item">
              <label>Phone</label>
              {isEditing ? (
                <input 
                  type="tel" 
                  value={editingData.phone || ''}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="input-modern"
                />
              ) : (
                <span>{userData.phone}</span>
              )}
            </div>
            <div className="detail-item">
              <label>Location</label>
              {isEditing ? (
                <input 
                  type="text" 
                  value={editingData.location || ''}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  className="input-modern"
                />
              ) : (
                <span>{userData.location}</span>
              )}
            </div>
          </div>
        </div>

        <div className="detail-section">
          <h3>Professional Information</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Institution</label>
              {isEditing ? (
                <input 
                  type="text" 
                  value={editingData.institution || ''}
                  onChange={(e) => handleInputChange('institution', e.target.value)}
                  className="input-modern"
                  placeholder="Enter your institution"
                />
              ) : (
                <span>{userData.institution || 'Not specified'}</span>
              )}
            </div>
            <div className="detail-item">
              <label>Role</label>
              {isEditing ? (
                <input 
                  type="text" 
                  value={editingData.role || ''}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  className="input-modern"
                  placeholder="Enter your role"
                />
              ) : (
                <span>{userData.role || 'Not specified'}</span>
              )}
            </div>
            <div className="detail-item">
              <label>Department</label>
              {isEditing ? (
                <input 
                  type="text" 
                  value={editingData.department || ''}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                  className="input-modern"
                  placeholder="Enter your department"
                />
              ) : (
                <span>{userData.department || 'Not specified'}</span>
              )}
            </div>
            <div className="detail-item">
              <label>Specialization</label>
              {isEditing ? (
                <input 
                  type="text" 
                  value={editingData.specialization || ''}
                  onChange={(e) => handleInputChange('specialization', e.target.value)}
                  className="input-modern"
                  placeholder="Enter your specialization"
                />
              ) : (
                <span>{userData.specialization || 'Not specified'}</span>
              )}
            </div>
            <div className="detail-item">
              <label>Member Since</label>
              <span>{userData.joinDate || 'Not specified'}</span>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <h3>Research Interests</h3>
          {isEditing ? (
            <div className="research-interests-editor">
              <div className="interests-input-container">
                <input
                  type="text"
                  placeholder="Add a new research interest"
                  className="input-modern"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      const newInterest = e.target.value.trim();
                      if (!editingData.researchInterests?.includes(newInterest)) {
                        handleInputChange('researchInterests', [...(editingData.researchInterests || []), newInterest]);
                        e.target.value = '';
                      }
                    }
                  }}
                />
                <Button 
                  className="btn-modern"
                  onClick={() => {
                    const input = document.querySelector('.interests-input-container input');
                    if (input && input.value.trim()) {
                      const newInterest = input.value.trim();
                      if (!editingData.researchInterests?.includes(newInterest)) {
                        handleInputChange('researchInterests', [...(editingData.researchInterests || []), newInterest]);
                        input.value = '';
                      }
                    }
                  }}
                  sx={getButtonStyles(false, isDarkMode, 'generic')}
                >
                  Add
                </Button>
              </div>
              <div className="interests-tags">
                {editingData.researchInterests?.map((interest, index) => (
                  <span key={index} className="interest-tag">
                    {interest}
                    <button
                      className="remove-interest"
                      onClick={() => {
                        const newInterests = editingData.researchInterests.filter((_, i) => i !== index);
                        handleInputChange('researchInterests', newInterests);
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div className="interests-tags">
              {userData.researchInterests?.map((interest, index) => (
                <span key={index} className="interest-tag">
                  {interest}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderSettingsTab = () => (
    <div className="settings-content">
      <div className="settings-section" style={{ opacity: 0.5, pointerEvents: 'none' }}>
        <h3>Notification Preferences <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 'normal' }}>(Coming Soon)</span></h3>
        <div className="settings-list">
          <div className="setting-row">
            <div className="setting-info">
              <label>Email Notifications</label>
              <p>Receive email updates about your research and collaborations</p>
            </div>
            <label className="switch">
              <input
                type="checkbox"
                checked={settings.emailNotifications}
                disabled
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="setting-row">
            <div className="setting-info">
              <label>Research Updates</label>
              <p>Get notified about new research in your field</p>
            </div>
            <label className="switch">
              <input
                type="checkbox"
                checked={settings.researchUpdates}
                disabled
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="setting-row">
            <div className="setting-info">
              <label>Weekly Digest</label>
              <p>Receive a weekly summary of your research activities</p>
            </div>
            <label className="switch">
              <input
                type="checkbox"
                checked={settings.weeklyDigest}
                disabled
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>Application Settings</h3>
        <div className="settings-list">
          <div className="setting-row">
            <div className="setting-info">
              <label>Dark Mode</label>
              <p>Use dark theme for better eye comfort</p>
              <div className="theme-status">
                <span className="current-theme">
                  Current theme: <strong>{isDarkMode ? 'Dark' : 'Light'}</strong>
                </span>
              </div>
            </div>
            <label className="switch">
              <input
                type="checkbox"
                checked={isDarkMode}
                onChange={() => {
                  console.log('🎨 Profile toggle clicked, current isDarkMode:', isDarkMode);
                  toggleTheme();
                }}
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSecurityTab = () => (
    <div className="security-content">
      <div className="security-section" style={{ opacity: 0.5, pointerEvents: 'none' }}>
        <h3>Authentication & Security <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 'normal' }}>(Coming Soon)</span></h3>
        <div className="security-list">
          <div className="security-row">
            <div className="security-info">
              <label>Two-Factor Authentication</label>
              <p>Add an extra layer of security to your account</p>
            </div>
            <label className="switch">
              <input
                type="checkbox"
                checked={securitySettings.twoFactorAuth}
                disabled
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="security-row">
            <div className="security-info">
              <label>Login Notifications</label>
              <p>Get notified of new login attempts</p>
            </div>
            <label className="switch">
              <input
                type="checkbox"
                checked={securitySettings.loginNotifications}
                disabled
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>
      </div>

      <div className="security-section" style={{ opacity: 0.5, pointerEvents: 'none' }}>
        <h3>Session Management <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 'normal' }}>(Coming Soon)</span></h3>
        <div className="security-list">
          <div className="security-row">
            <div className="security-info">
              <label>Session Timeout (minutes)</label>
              <select 
                value={securitySettings.sessionTimeout}
                disabled
                className="select-modern"
              >
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={60}>1 hour</option>
                <option value={120}>2 hours</option>
                <option value={0}>Never</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="security-section">
        <h3>Account Actions</h3>
        <div className="security-list">
          <div className="security-row">
            <div className="security-actions">
              <Button 
                className="btn-modern"
                onClick={handleOpenPasswordDialog}
                startIcon={<WarningIcon />}
                sx={getButtonStyles(false, isDarkMode, 'generic')}
              >
                Change Password
              </Button>
              <Button 
                className="btn-modern"
                startIcon={<SecurityIcon />}
                sx={getButtonStyles(false, isDarkMode, 'delete')}
              >
                Deactivate Account
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="profile-page">
      
      {/* Main Content */}
      <div 
        className="profile-container"
        style={{
          overflow: 'visible',
          height: 'auto'
        }}
      >
        {/* Profile & Settings Container */}
        <div style={{ marginTop: '6rem' }}>
          {/* Title above buttons and card */}
          <div style={{ marginBottom: '1.5rem', alignSelf: 'flex-start', width: '100%' }}>
            <h1 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '1.8rem', fontWeight: '600', textAlign: 'left', marginLeft: '2.5rem' }}>
              Profile & Settings
            </h1>
            <p style={{ color: 'var(--text-muted)', margin: '0.5rem 0 0 0', fontSize: '1rem', textAlign: 'left', marginLeft: '2.5rem' }}>
              Manage your account and preferences
            </p>
          </div>

          {/* Navigation Tabs and Content Container */}
          <div className="profile-tabs-container">
          {/* Navigation Tabs */}
          <div className="profile-tabs">
            <button 
              className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <PersonIcon sx={{ fontSize: '1.2rem' }} /> Profile
            </button>
            <button 
              className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <SettingsIcon sx={{ fontSize: '1.2rem' }} /> Settings
            </button>
            <button 
              className={`tab-button ${activeTab === 'security' ? 'active' : ''}`}
              onClick={() => setActiveTab('security')}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <SecurityIcon sx={{ fontSize: '1.2rem' }} /> Security
            </button>
          </div>

          {/* Tab Content */}
          <div 
            className="tab-content"
            data-tab={activeTab}
          >
            {activeTab === 'profile' && renderProfileTab()}
            {activeTab === 'settings' && renderSettingsTab()}
            {activeTab === 'security' && renderSecurityTab()}
          </div>
        </div>
        </div> {/* End Profile & Settings Container */}

        {/* Styled Snackbar for Notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity}
            variant="filled"
            sx={{
              width: '100%',
              backgroundColor: snackbar.severity === 'warning' 
                ? 'rgba(255, 152, 0, 0.9)' 
                : snackbar.severity === 'error'
                ? 'rgba(244, 67, 54, 0.9)'
                : snackbar.severity === 'success'
                ? 'rgba(70, 130, 48, 0.9)'
                : 'rgba(108, 48, 130, 0.9)',
              color: '#ffffff',
              border: snackbar.severity === 'warning'
                ? '1px solid rgba(255, 152, 0, 0.5)'
                : snackbar.severity === 'error'
                ? '1px solid rgba(244, 67, 54, 0.5)'
                : snackbar.severity === 'success'
                ? '1px solid rgba(70, 130, 48, 0.5)'
                : '1px solid rgba(108, 48, 130, 0.5)',
              borderRadius: 'var(--radius-md)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              '& .MuiAlert-icon': {
                color: '#ffffff'
              }
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Password Change Dialog */}
        <Dialog 
          open={passwordDialog.open} 
          onClose={handleClosePasswordDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: 'var(--surface-2)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-lg)',
              backdropFilter: 'blur(20px)'
            }
          }}
        >
          <DialogTitle sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
            Change Password
          </DialogTitle>
          <DialogContent>
            {passwordDialog.error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  background: 'rgba(244, 67, 54, 0.1)',
                  border: '1px solid rgba(244, 67, 54, 0.3)',
                  color: '#ff6b6b'
                }}
              >
                {passwordDialog.error}
              </Alert>
            )}
            
            <TextField
              fullWidth
              margin="dense"
              label="Current Password"
              type={passwordDialog.showCurrentPassword ? 'text' : 'password'}
              value={passwordDialog.currentPassword}
              onChange={(e) => setPasswordDialog({ ...passwordDialog, currentPassword: e.target.value })}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--surface)',
                  '& fieldset': { borderColor: 'var(--border)' },
                  '&:hover fieldset': { borderColor: 'var(--neon-cyan)' },
                  '&.Mui-focused fieldset': { borderColor: 'var(--neon-cyan)' }
                },
                '& .MuiInputLabel-root': {
                  color: 'var(--text-muted)',
                  '&.Mui-focused': { color: 'var(--neon-cyan)' }
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setPasswordDialog({ ...passwordDialog, showCurrentPassword: !passwordDialog.showCurrentPassword })}
                      edge="end"
                      sx={{ color: 'var(--text-muted)' }}
                    >
                      {passwordDialog.showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <TextField
              fullWidth
              margin="dense"
              label="New Password"
              type={passwordDialog.showNewPassword ? 'text' : 'password'}
              value={passwordDialog.newPassword}
              onChange={(e) => setPasswordDialog({ ...passwordDialog, newPassword: e.target.value })}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--surface)',
                  '& fieldset': { borderColor: 'var(--border)' },
                  '&:hover fieldset': { borderColor: 'var(--neon-cyan)' },
                  '&.Mui-focused fieldset': { borderColor: 'var(--neon-cyan)' }
                },
                '& .MuiInputLabel-root': {
                  color: 'var(--text-muted)',
                  '&.Mui-focused': { color: 'var(--neon-cyan)' }
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setPasswordDialog({ ...passwordDialog, showNewPassword: !passwordDialog.showNewPassword })}
                      edge="end"
                      sx={{ color: 'var(--text-muted)' }}
                    >
                      {passwordDialog.showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <TextField
              fullWidth
              margin="dense"
              label="Confirm New Password"
              type={passwordDialog.showConfirmPassword ? 'text' : 'password'}
              value={passwordDialog.confirmPassword}
              onChange={(e) => setPasswordDialog({ ...passwordDialog, confirmPassword: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--surface)',
                  '& fieldset': { borderColor: 'var(--border)' },
                  '&:hover fieldset': { borderColor: 'var(--neon-cyan)' },
                  '&.Mui-focused fieldset': { borderColor: 'var(--neon-cyan)' }
                },
                '& .MuiInputLabel-root': {
                  color: 'var(--text-muted)',
                  '&.Mui-focused': { color: 'var(--neon-cyan)' }
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setPasswordDialog({ ...passwordDialog, showConfirmPassword: !passwordDialog.showConfirmPassword })}
                      edge="end"
                      sx={{ color: 'var(--text-muted)' }}
                    >
                      {passwordDialog.showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </DialogContent>
          <DialogActions sx={{ padding: '16px 24px' }}>
            <Button 
              onClick={handleClosePasswordDialog}
              className="btn-modern"
              sx={getButtonStyles(false, isDarkMode, 'generic')}
            >
              Cancel
            </Button>
            <Button 
              onClick={handlePasswordChange}
              disabled={passwordDialog.loading}
              className="btn-modern"
              sx={{
                ...getButtonStyles(false, isDarkMode, 'generic'),
                '&:disabled': {
                  opacity: 0.6
                }
              }}
            >
              {passwordDialog.loading ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
}

export default Profile;
